import { getWallAxisXZ } from './wallGeometryUtils';

/**
 * Room-plan breathing room, in metres per side: measurements sit 0.3 m off the
 * wall (`offsetDistance`, measurementUtilsV2) and their labels are drawn at a
 * fixed pixel size, so 0.4 m covers the text at the scales we print.
 */
export const ROOM_PLAN_MEASUREMENT_BUFFER_M = 0.7;

/**
 * The world extent (metres) of what a room floor plan actually draws.
 *
 * ONE source for it, because two copies drifted: `renderFloorPlanToImage`
 * reads the outline off `floors[].polygonCorners`, while `RoomPackageCreator`
 * read `room.polygonCorners` — a field no room doc carries (verified on dev).
 * Both of its dimensions fell back to a 5 m default, so a 18'6" x 43'1"
 * kitchen chose 1/2" = 1' and printed a clipped strip of itself.
 *
 * Corners are `{x, y, z}` on every room in the corpus; the array forms are
 * legacy shapes the drawing code has always tolerated ([x, z] and [x, y, z]).
 *
 * @param {Array} rooms  rooms to measure.
 * @param {{includeWalls?: boolean, bufferM?: number}} options
 *   `includeWalls` also spans the rooms' wall ends — a wall can run past the
 *   floor plate it borders. `bufferM` is added on EVERY side, for dimension
 *   lines and labels drawn outside the room.
 * @returns {{minX, maxX, minZ, maxZ, widthM, heightM}|null} null when nothing
 *   measurable was found — the caller decides what to do, rather than being
 *   handed an invented size.
 */
export const roomPlanBounds = (
  rooms,
  { includeWalls = true, bufferM = 0 } = {}
) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  const addPoint = (x, z) => {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  };

  const addCorner = (corner) => {
    if (!corner) return;
    if (Array.isArray(corner)) {
      addPoint(corner[0], corner.length > 2 ? corner[2] : corner[1]);
      return;
    }
    addPoint(corner.x, corner.z);
  };

  (rooms || []).forEach((room) => {
    (room?.floors || []).forEach((floor) =>
      (floor?.polygonCorners || []).forEach(addCorner)
    );

    if (!includeWalls) return;
    (room?.walls || []).forEach((wall) => {
      if (!wall?.center || !wall?.dimensions) return;
      const { dirX, dirZ, len, midX, midZ } = getWallAxisXZ(wall);
      const half = len / 2;
      addPoint(midX - dirX * half, midZ - dirZ * half);
      addPoint(midX + dirX * half, midZ + dirZ * half);
    });
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minZ)) return null;

  return {
    minX: minX - bufferM,
    maxX: maxX + bufferM,
    minZ: minZ - bufferM,
    maxZ: maxZ + bufferM,
    widthM: maxX - minX + bufferM * 2,
    heightM: maxZ - minZ + bufferM * 2,
  };
};
